import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/common/Button';
import { StatusBadge } from '../../components/common/StatusBadge';
import { ncaLayerService, SignatureResult } from '../../services/ncaLayerService';
import { contractService } from '../../services/contractService';
import apiService from '../../services/api';
import toast from 'react-hot-toast';

interface Company {
    id: number;
    bin: string;
    name: string;
    legalAddress: string;
    actualAddress: string;
    contactPhone: string;
    contactEmail: string;
    directorName: string;
    bank?: string;
    kbe?: string;
    bik?: string;
    accountNumber?: string;
}

interface ContractData {
    id?: number;
    contractNumber: string;
    companyData: Company;
    status: 'DRAFT' | 'PENDING_SIGNATURE' | 'SIGNED_BY_BUYER' | 'SIGNED_BY_SUPPLIER' | 'SIGNED_BY_BOTH' | 'SIGNED' | 'CANCELLED' | 'EXPIRED' | 'APPROVED' | 'PENDING_MODERATION' | 'NEEDS_REVISION';
    createdAt?: string;
    signedAt?: string;
    signatureData?: string;
    cmsZipFilePath?: string;
}

export const ContractPage: React.FC = () => {
    const { user } = useAuth();
    const [company, setCompany] = useState<Company | null>(null);
    const [contract, setContract] = useState<ContractData | null>(null);
    const [allContracts, setAllContracts] = useState<ContractData[]>([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [signing, setSigning] = useState(false);
    const [certificates, setCertificates] = useState<any[]>([]);
    const [selectedCertificate, setSelectedCertificate] = useState<string>('');
    const [certificatePassword, setCertificatePassword] = useState<string>('');
    const [ncaLayerStatus, setNcaLayerStatus] = useState<'checking' | 'available' | 'unavailable'>('checking');

    useEffect(() => {
        loadData();
        checkNCALayer();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);

            // Загружаем данные компании
            const companyResponse = await apiService.getMyCompany();
            if (companyResponse.data.success && companyResponse.data.data) {
                setCompany(companyResponse.data.data);

                // Загружаем все существующие договоры
                await loadAllContracts();

                // Создаем или загружаем договор для компании
                await loadOrCreateContract(companyResponse.data.data);
            }
        } catch (error: any) {
            console.error('Ошибка загрузки данных:', error);
            toast.error(error.message || 'Ошибка загрузки данных');
        } finally {
            setLoading(false);
        }
    };

    const loadAllContracts = async () => {
        try {
            const contracts = await contractService.getUserContracts();
            setAllContracts(contracts);
            return contracts;
        } catch (error: any) {
            console.error('Ошибка загрузки списка договоров:', error);
            return [];
        }
    };

    const loadOrCreateContract = async (companyData: Company) => {
        try {
            // Загружаем все договоры пользователя
            const contracts = await loadAllContracts();
            
            console.log('📋 Загруженные договоры:', contracts);
            console.log('📋 Статусы договоров:', contracts.map((c: any) => ({ id: c.id, status: c.status })));
            
            // Приоритет отображения:
            // 1. Договор подписанный покупателем (SIGNED_BY_BUYER) - ждёт подписи поставщика (ВЫСШИЙ ПРИОРИТЕТ)
            // 2. Договор ожидающий подписания (PENDING_SIGNATURE, APPROVED)
            // 3. Полностью подписанный договор (SIGNED_BY_BOTH)
            // 4. Если ничего нет - показываем форму генерации
            
            // ВАЖНО: Сначала проверяем SIGNED_BY_BUYER, чтобы нельзя было подписать повторно
            const signedByBuyerContract = contracts.find((c: any) => 
                c.status === 'SIGNED_BY_BUYER'
            );
            
            const pendingContract = contracts.find((c: any) => 
                c.status === 'PENDING_SIGNATURE' || c.status === 'APPROVED'
            );
            
            const fullySignedContract = contracts.find((c: any) => 
                c.status === 'SIGNED_BY_BOTH' || c.status === 'SIGNED'
            );
            
            // ВАЖНО: Сначала проверяем SIGNED_BY_BUYER чтобы заблокировать повторное подписание
            if (signedByBuyerContract) {
                // Договор подписан покупателем - ждёт поставщика
                const contractData: ContractData = {
                    id: signedByBuyerContract.id,
                    contractNumber: signedByBuyerContract.contractNumber,
                    companyData: companyData,
                    status: signedByBuyerContract.status,
                    createdAt: signedByBuyerContract.createdAt,
                    signedAt: signedByBuyerContract.signedAt,
                    signatureData: signedByBuyerContract.signatureData,
                    cmsZipFilePath: signedByBuyerContract.cmsZipFilePath
                };
                setContract(contractData);
                console.log('✅ Показываем договор SIGNED_BY_BUYER:', contractData);
            } else if (pendingContract) {
                // Есть договор ожидающий подписания - показываем его
                const contractData: ContractData = {
                    id: pendingContract.id,
                    contractNumber: pendingContract.contractNumber,
                    companyData: companyData,
                    status: pendingContract.status,
                    createdAt: pendingContract.createdAt,
                    signedAt: pendingContract.signedAt,
                    signatureData: pendingContract.signatureData,
                    cmsZipFilePath: pendingContract.cmsZipFilePath
                };
                setContract(contractData);
                console.log('✅ Показываем договор PENDING_SIGNATURE:', contractData);
            } else if (fullySignedContract) {
                // Есть полностью подписанный договор - показываем его (генерация недоступна)
                const contractData: ContractData = {
                    id: fullySignedContract.id,
                    contractNumber: fullySignedContract.contractNumber,
                    companyData: companyData,
                    status: fullySignedContract.status,
                    createdAt: fullySignedContract.createdAt,
                    signedAt: fullySignedContract.signedAt,
                    signatureData: fullySignedContract.signatureData,
                    cmsZipFilePath: fullySignedContract.cmsZipFilePath
                };
                setContract(contractData);
                console.log('✅ Показываем договор SIGNED_BY_BOTH:', contractData);
            } else {
                // Нет активных договоров - создаем черновик для генерации
                const newContract: ContractData = {
                    contractNumber: generateContractNumber(),
                    companyData: companyData,
                    status: 'DRAFT'
                };
                setContract(newContract);
                console.log('✅ Создан черновик DRAFT:', newContract);
            }
        } catch (error: any) {
            console.error('Ошибка работы с договором:', error);
            toast.error('Ошибка работы с договором');
        }
    };

    const generateContractNumber = (): string => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');

        return `CONTRACT-${year}${month}${day}-${random}`;
    };

    const checkNCALayer = async () => {
        try {
            setNcaLayerStatus('checking');

            // isAvailable() сам создаёт соединение если нужно - не нужен отдельный start()
            const isAvailable = await ncaLayerService.isAvailable();

            if (isAvailable) {
                setNcaLayerStatus('available');
                await loadCertificates();
            } else {
                setNcaLayerStatus('unavailable');
            }
        } catch (error) {
            console.error('Ошибка проверки NCALayer:', error);
            setNcaLayerStatus('unavailable');
        }
    };

    const loadCertificates = async () => {
        try {
            const certs = await ncaLayerService.getCertificates();
            setCertificates(certs);
            if (certs.length > 0) {
                // Автоматически выбираем PKCS12 если доступен
                const pkcs12Cert = certs.find(cert => cert.path === 'PKCS12');
                setSelectedCertificate(pkcs12Cert?.path || certs[0].path);
            }
        } catch (error) {
            console.error('Ошибка загрузки сертификатов:', error);
            toast.error('Ошибка загрузки сертификатов');
        }
    };

    const handleGenerateContract = async () => {
        if (!contract || !company) return;

        try {
            setGenerating(true);
            toast.loading('Генерация договора...', { id: 'generating' });

            const response = await contractService.generateContract();

            if (response.success && response.data) {
                // Преобразуем данные из API в формат ContractData
                const contractData: ContractData = {
                    id: response.data.id,
                    contractNumber: response.data.contractNumber,
                    companyData: company!, // Используем данные компании из состояния
                    status: response.data.status,
                    createdAt: response.data.createdAt,
                    signedAt: response.data.signedAt,
                    signatureData: response.data.signatureData
                };

                setContract(contractData);
                toast.success('Договор успешно сгенерирован!', { id: 'generating' });

                // Автоматически скачиваем PDF для ознакомления
                if (response.data.id) {
                    setTimeout(() => {
                        handleDownloadContract();
                    }, 1000);
                }
            } else {
                throw new Error(response.message || 'Ошибка генерации договора');
            }
        } catch (error: any) {
            console.error('Ошибка генерации договора:', error);
            toast.error(error.message || 'Ошибка генерации договора', { id: 'generating' });
        } finally {
            setGenerating(false);
        }
    };

    const handleDownloadContract = async () => {
        if (!contract || !contract.id) return;

        try {
            toast.loading('Скачивание договора...', { id: 'download' });

            const blob = await contractService.downloadContract(contract.id);

            // Создаем ссылку для скачивания
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${contract.contractNumber}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success('Договор скачан!', { id: 'download' });
        } catch (error: any) {
            console.error('Ошибка скачивания договора:', error);
            toast.error(error.message || 'Ошибка скачивания договора', { id: 'download' });
        }
    };

    const handleDownloadCmsZip = async () => {
        if (!contract || !contract.id || !contract.cmsZipFilePath) return;

        try {
            toast.loading('Скачивание CMS архива...', { id: 'download-cms' });

            await contractService.downloadCmsZipFile(contract.id, contract.contractNumber);

            toast.success('CMS архив скачан!', { id: 'download-cms' });
        } catch (error: any) {
            console.error('Ошибка скачивания CMS архива:', error);
            toast.error(error.message || 'Ошибка скачивания CMS архива', { id: 'download-cms' });
        }
    };

    const handleDownloadCmsZipFromHistory = async (contractId: number, contractNumber: string) => {
        try {
            toast.loading('Скачивание CMS архива...', { id: 'download-cms-history' });

            await contractService.downloadCmsZipFile(contractId, contractNumber);

            toast.success('CMS архив скачан!', { id: 'download-cms-history' });
        } catch (error: any) {
            console.error('Ошибка скачивания CMS архива:', error);
            toast.error(error.message || 'Ошибка скачивания CMS архива', { id: 'download-cms-history' });
        }
    };

    // Функция для извлечения БИН из сертификата
    const extractBinFromCertificate = (signerInfo: SignatureResult['signerInfo']): string | null => {
        // БИН может быть в serialNumber в формате "BIN123456789012" или "IIN123456789012"
        // Для юр. лиц используется BIN (12 цифр)
        const serialNumber = signerInfo.serialNumber || '';
        
        // Ищем BIN в serialNumber
        const binMatch = serialNumber.match(/BIN(\d{12})/i);
        if (binMatch) {
            return binMatch[1];
        }
        
        // Если BIN не найден, проверяем organization на наличие БИН
        // Иногда БИН может быть в названии организации
        const orgBinMatch = (signerInfo.organization || '').match(/(\d{12})/);
        if (orgBinMatch) {
            return orgBinMatch[1];
        }
        
        // Также проверяем просто 12 цифр в serialNumber (без префикса)
        const pureNumberMatch = serialNumber.match(/^(\d{12})$/);
        if (pureNumberMatch) {
            return pureNumberMatch[1];
        }
        
        return null;
    };

    const handleSignContract = async () => {
        if (!contract || !company) return;

        try {
            setSigning(true);

            // Проверяем доступность NCALayer
            if (ncaLayerStatus !== 'available') {
                toast.error('NCALayer недоступен. Установите NCALayer и запустите его.');
                setSigning(false);
                return;
            }

            // Используем выбранный сертификат или PKCS12 по умолчанию
            const certPath = selectedCertificate || 'PKCS12';

            // Подготавливаем данные для подписания
            const contractData = JSON.stringify({
                contractId: contract.id,
                contractNumber: contract.contractNumber,
                companyBin: company.bin,
                companyName: company.name,
                timestamp: new Date().toISOString()
            });

            toast.loading('Подписание договора...', { id: 'signing' });

            // Подписываем данные через NCALayer (один вызов)
            const signatureResult: SignatureResult = await ncaLayerService.signData(
                contractData,
                certPath,
                certificatePassword
            );

            // Проверяем БИН из сертификата
            const certificateBin = extractBinFromCertificate(signatureResult.signerInfo);
            console.log('🔍 Проверка БИН:', {
                certificateBin,
                companyBin: company.bin,
                signerInfo: signatureResult.signerInfo
            });

            if (certificateBin && certificateBin !== company.bin) {
                toast.error(
                    `БИН в сертификате (${certificateBin}) не соответствует БИН компании (${company.bin}). Используйте ЭЦП, выданную на вашу компанию.`,
                    { id: 'signing', duration: 6000 }
                );
                setSigning(false);
                return;
            }

            // Если БИН не удалось извлечь, показываем предупреждение но продолжаем
            if (!certificateBin) {
                console.warn('⚠️ Не удалось извлечь БИН из сертификата, проверка пропущена');
            }

            // Отправляем подпись на сервер с компактным штампом покупателя
            const signResponse = await contractService.signContractCustomer(contract.id!, {
                signatureData: signatureResult.signature,
                certificateData: signatureResult.certificate,
                signerInfo: JSON.stringify(signatureResult.signerInfo),
                signerName: signatureResult.signerInfo.commonName,
                organizationName: signatureResult.signerInfo.organization || company.name,
                signDate: new Date().toLocaleString('ru-RU')
            });

            if (signResponse.success) {
                toast.success('Договор успешно подписан!', { id: 'signing' });

                // Перезагружаем данные договора с сервера чтобы получить актуальный статус
                await loadData();
            } else {
                throw new Error(signResponse.message || 'Ошибка сохранения подписи');
            }
        } catch (error: any) {
            console.error('Ошибка подписания договора:', error);
            toast.error(error.message || 'Ошибка подписания договора', { id: 'signing' });
        } finally {
            setSigning(false);
        }
    };

    const getStatusDisplayName = (status: string) => {
        const statusNames: Record<string, string> = {
            'DRAFT': 'Черновик',
            'PENDING_SIGNATURE': 'Ожидает подписания',
            'SIGNED_BY_BUYER': 'Подписан покупателем',
            'SIGNED_BY_SUPPLIER': 'Подписан поставщиком',
            'SIGNED_BY_BOTH': 'Подписан обеими сторонами',
            'SIGNED': 'Подписан',
            'CANCELLED': 'Отменен',
            'EXPIRED': 'Истек срок действия',
            'APPROVED': 'Одобрен',
            'PENDING_MODERATION': 'На модерации',
            'NEEDS_REVISION': 'Требует доработки'
        };
        return statusNames[status] || status;
    };

    const getStatusColor = (status: string) => {
        const statusColors: Record<string, string> = {
            'DRAFT': 'bg-gray-100 text-gray-800',
            'PENDING_SIGNATURE': 'bg-yellow-100 text-yellow-800',
            'SIGNED_BY_BUYER': 'bg-blue-100 text-blue-800',
            'SIGNED_BY_SUPPLIER': 'bg-indigo-100 text-indigo-800',
            'SIGNED_BY_BOTH': 'bg-green-100 text-green-800',
            'SIGNED': 'bg-green-100 text-green-800',
            'CANCELLED': 'bg-red-100 text-red-800',
            'EXPIRED': 'bg-orange-100 text-orange-800',
            'APPROVED': 'bg-green-100 text-green-800',
            'PENDING_MODERATION': 'bg-yellow-100 text-yellow-800',
            'NEEDS_REVISION': 'bg-amber-100 text-amber-800'
        };
        return statusColors[status] || 'bg-gray-100 text-gray-800';
    };

    const handleDownloadContractById = async (contractId: number, contractNumber: string) => {
        try {
            toast.loading('Скачивание договора...', { id: 'download-contract' });
            await contractService.downloadContractById(contractId, contractNumber);
            toast.success('Договор скачан!', { id: 'download-contract' });
        } catch (error: any) {
            console.error('Ошибка скачивания договора:', error);
            toast.error(error.message || 'Ошибка скачивания договора', { id: 'download-contract' });
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Загрузка данных...</p>
                </div>
            </div>
        );
    }

    if (!company || !contract) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Данные не найдены</h2>
                    <p className="text-gray-600">Не удалось загрузить данные компании или договора</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Заголовок */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Договор поставки</h1>
                            <p className="mt-2 text-gray-600">
                                Договор № {contract.contractNumber}
                            </p>
                        </div>
                        <StatusBadge
                            status={contract.status}
                            text={getStatusDisplayName(contract.status)}
                            className={getStatusColor(contract.status)}
                        />
                    </div>
                </div>

                {/* Информация о компании */}
                <div className="bg-white shadow rounded-lg mb-8">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h3 className="text-lg font-medium text-gray-900">Данные заказчика</h3>
                    </div>
                    <div className="px-6 py-4">
                        <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                            <div>
                                <dt className="text-sm font-medium text-gray-500">Название компании</dt>
                                <dd className="mt-1 text-sm text-gray-900">{company.name}</dd>
                            </div>
                            <div>
                                <dt className="text-sm font-medium text-gray-500">БИН</dt>
                                <dd className="mt-1 text-sm text-gray-900">{company.bin}</dd>
                            </div>
                            <div>
                                <dt className="text-sm font-medium text-gray-500">Юридический адрес</dt>
                                <dd className="mt-1 text-sm text-gray-900">{company.legalAddress}</dd>
                            </div>
                            <div>
                                <dt className="text-sm font-medium text-gray-500">Фактический адрес</dt>
                                <dd className="mt-1 text-sm text-gray-900">{company.actualAddress}</dd>
                            </div>
                            <div>
                                <dt className="text-sm font-medium text-gray-500">Контактный телефон</dt>
                                <dd className="mt-1 text-sm text-gray-900">{company.contactPhone}</dd>
                            </div>
                            <div>
                                <dt className="text-sm font-medium text-gray-500">Контактный email</dt>
                                <dd className="mt-1 text-sm text-gray-900">{company.contactEmail}</dd>
                            </div>
                            <div>
                                <dt className="text-sm font-medium text-gray-500">Директор</dt>
                                <dd className="mt-1 text-sm text-gray-900">{company.directorName}</dd>
                            </div>
                            {company.bank && (
                                <div>
                                    <dt className="text-sm font-medium text-gray-500">Банк</dt>
                                    <dd className="mt-1 text-sm text-gray-900">{company.bank}</dd>
                                </div>
                            )}
                            {company.accountNumber && (
                                <div>
                                    <dt className="text-sm font-medium text-gray-500">Номер счета</dt>
                                    <dd className="mt-1 text-sm text-gray-900">{company.accountNumber}</dd>
                                </div>
                            )}
                        </dl>
                    </div>
                </div>

                {/* Информация о договоре */}
                <div className="bg-white shadow rounded-lg mb-8">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h3 className="text-lg font-medium text-gray-900">
                            {contract.status === 'DRAFT' 
                                ? 'Генерация договора' 
                                : contract.status === 'SIGNED_BY_BOTH' || contract.status === 'SIGNED'
                                    ? 'Договор подписан'
                                    : contract.status === 'SIGNED_BY_BUYER'
                                        ? 'Ожидание подписи поставщика'
                                        : 'Подписание договора'
                            }
                        </h3>
                    </div>
                    <div className="px-6 py-4">
                        {/* Полностью подписанный договор */}
                        {(contract.status === 'SIGNED_BY_BOTH' || contract.status === 'SIGNED') && (
                            <div className="text-center">
                                <div className="mb-6 p-6 bg-green-50 border-2 border-green-200 rounded-lg">
                                    <div className="flex items-center justify-center mb-4">
                                        <svg className="h-16 w-16 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <h4 className="text-xl font-bold text-green-800 mb-2">
                                        Договор полностью подписан!
                                    </h4>
                                    <p className="text-green-700 mb-4">
                                        Договор № {contract.contractNumber} подписан обеими сторонами.<br/>
                                        Вы можете создавать заказы на поставку продукции.
                                    </p>
                                    {contract.signedAt && (
                                        <p className="text-sm text-green-600">
                                            Дата подписания: {new Date(contract.signedAt).toLocaleDateString('ru-RU')}
                                        </p>
                                    )}
                                </div>
                                <div className="space-y-3">
                                    <Button
                                        onClick={handleDownloadContract}
                                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg w-full font-medium"
                                    >
                                        Скачать подписанный договор (PDF)
                                    </Button>
                                    {contract.cmsZipFilePath && (
                                        <Button
                                            onClick={handleDownloadCmsZip}
                                            className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg w-full flex items-center justify-center font-medium"
                                        >
                                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                            </svg>
                                            Скачать CMS подписи (ZIP)
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Договор подписан покупателем - ждёт поставщика */}
                        {contract.status === 'SIGNED_BY_BUYER' && (
                            <div className="text-center">
                                <div className="mb-6 p-6 bg-blue-50 border-2 border-blue-200 rounded-lg">
                                    <div className="flex items-center justify-center mb-4">
                                        <svg className="h-16 w-16 text-blue-500 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <h4 className="text-xl font-bold text-blue-800 mb-2">
                                        Ожидается подпись поставщика
                                    </h4>
                                    <p className="text-blue-700 mb-2">
                                        Договор № {contract.contractNumber} подписан с вашей стороны.
                                    </p>
                                    <p className="text-blue-600 text-sm">
                                        После подписания поставщиком вы сможете создавать заказы.
                                    </p>
                                </div>
                                <Button
                                    onClick={handleDownloadContract}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg w-full font-medium"
                                >
                                    Скачать договор (PDF)
                                </Button>
                            </div>
                        )}

                        {/* Договор ожидает подписания */}
                        {(contract.status === 'PENDING_SIGNATURE' || contract.status === 'APPROVED') && (
                            <div className="text-center">
                                <div className="border-2 border-dashed border-yellow-300 rounded-lg p-8 bg-yellow-50">
                                    <div className="text-yellow-500 mb-4">
                                        <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                        </svg>
                                    </div>
                                    <h4 className="text-lg font-medium text-gray-900 mb-2">
                                        Договор готов к подписанию
                                    </h4>
                                    <p className="text-gray-600 mb-4">
                                        Договор № {contract.contractNumber} сгенерирован и ожидает вашей электронной подписи.
                                    </p>
                                    <Button
                                        onClick={handleDownloadContract}
                                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
                                    >
                                        Скачать договор для ознакомления (PDF)
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Черновик - можно генерировать */}
                        {contract.status === 'DRAFT' && (
                            <div className="text-center">
                                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8">
                                    <div className="text-gray-400 mb-4">
                                        <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                    </div>
                                    <h4 className="text-lg font-medium text-gray-900 mb-2">
                                        Договор готов к генерации
                                    </h4>
                                    <p className="text-gray-500 mb-4">
                                        Нажмите "Сгенерировать договор" для создания PDF документа.<br/>
                                        После генерации договор будет автоматически скачан для ознакомления.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Электронная подпись */}
                {(contract.status === 'PENDING_SIGNATURE' || contract.status === 'APPROVED') && (
                    <div className="bg-white shadow rounded-lg mb-8">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h3 className="text-lg font-medium text-gray-900">Электронная подпись</h3>
                        </div>
                        <div className="px-6 py-4">
                            {/* Статус NCALayer */}
                            <div className="mb-6">
                                <div className="flex items-center">
                                    <div className={`w-3 h-3 rounded-full mr-3 ${ncaLayerStatus === 'available' ? 'bg-green-500' :
                                        ncaLayerStatus === 'unavailable' ? 'bg-red-500' : 'bg-yellow-500'
                                        }`}></div>
                                    <span className="text-sm font-medium text-gray-700">
                                        NCALayer: {
                                            ncaLayerStatus === 'available' ? 'Доступен' :
                                                ncaLayerStatus === 'unavailable' ? 'Недоступен' : 'Проверка...'
                                        }
                                    </span>
                                </div>
                                {ncaLayerStatus === 'unavailable' && (
                                    <div className="mt-2">
                                        <p className="text-sm text-red-600 mb-2">
                                            Установите и запустите NCALayer для подписания документов
                                        </p>
                                        <button
                                            onClick={checkNCALayer}
                                            className="px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 transition-colors text-sm"
                                        >
                                            Проверить снова
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Выбор сертификата */}
                            {ncaLayerStatus === 'available' && (
                                <div className="space-y-4">
                                    {certificates.length > 0 ? (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Выберите сертификат
                                            </label>
                                            <select
                                                value={selectedCertificate}
                                                onChange={(e) => setSelectedCertificate(e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            >
                                                {certificates.map((cert, index) => (
                                                    <option key={index} value={cert.path}>
                                                        {cert.alias} - {cert.subject}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    ) : (
                                        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                                            Сертификаты не найдены. Убедитесь, что в NCALayer установлены сертификаты ЭЦП.
                                        </div>
                                    )}

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Пароль сертификата (если требуется)
                                        </label>
                                        <input
                                            type="password"
                                            value={certificatePassword}
                                            onChange={(e) => setCertificatePassword(e.target.value)}
                                            placeholder="Введите пароль сертификата"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                </div>
                            )}


                        </div>
                    </div>
                )}

                {/* Инструкции - показываем только если договор ещё не подписан полностью */}
                {contract.status !== 'SIGNED_BY_BOTH' && contract.status !== 'SIGNED' && contract.status !== 'SIGNED_BY_BUYER' && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-blue-800">
                                    Инструкции по работе с договором
                                </h3>
                                <div className="mt-2 text-sm text-blue-700">
                                    <ul className="list-disc list-inside space-y-1">
                                        {contract.status === 'DRAFT' && (
                                            <>
                                                <li>Нажмите "Сгенерировать договор" для создания PDF документа</li>
                                                <li>PDF будет автоматически скачан для ознакомления</li>
                                                <li>После ознакомления договор будет готов к подписанию</li>
                                            </>
                                        )}
                                        {(contract.status === 'PENDING_SIGNATURE' || contract.status === 'APPROVED') && (
                                            <>
                                                <li>Убедитесь, что у вас установлен NCALayer</li>
                                                <li>Проверьте действительность вашего сертификата ЭЦП</li>
                                                <li>Скачайте договор и внимательно ознакомьтесь с его содержанием</li>
                                                <li>Нажмите "Подписать договор" для начала процесса подписания</li>
                                            </>
                                        )}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Действия */}
                <div className="flex justify-center space-x-4">
                    {contract.status === 'DRAFT' && (
                        <Button
                            onClick={handleGenerateContract}
                            disabled={generating}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
                        >
                            {generating ? 'Генерация...' : 'Сгенерировать договор'}
                        </Button>
                    )}

                    {(contract.status === 'PENDING_SIGNATURE' || contract.status === 'APPROVED') && (
                        <Button
                            onClick={handleSignContract}
                            disabled={signing || ncaLayerStatus !== 'available'}
                            className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
                        >
                            {signing ? 'Подписание...' : 'Подписать договор'}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};
